import {
  ActionIcon,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Group,
  Space,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconList,
  IconMap,
  IconX,
} from "@tabler/icons-react";
import {
  AdvancedMarker,
  APILoadingStatus,
  Map as GoogleMap,
  useApiLoadingStatus,
} from "@vis.gl/react-google-maps";
import { useState } from "react";
import { Link } from "wouter";
import { client } from "@/api-client";
import type { TrainerPageOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Portrait } from "@/components/Portrait";
import { specialtyOptions } from "@/pages/trainer/profile/common";
import { createPath, UniversalRoutes } from "@/routers/routes";
import { getCurrentCountry } from "@/utils/country";

const sessionTypeLabel = (trainer: TrainerPageOutput) => {
  if (trainer.inPerson && trainer.virtual) {
    return "In Person and Virtual";
  }
  if (trainer.inPerson) {
    return "In Person Only";
  }
  if (trainer.virtual) {
    return "Virtual Only";
  }
  return "No Sessions";
};

const TrainerCard = ({
  trainer,
  withCloseButton = false,
  onClose,
}: {
  trainer: TrainerPageOutput;
  withCloseButton?: boolean;
  onClose?: () => void;
}) => (
  <Card shadow="sm" padding="lg" radius="md" withBorder>
    <Card.Section>
      <Group p="md" style={{ position: "relative" }}>
        {withCloseButton && (
          <ActionIcon
            variant="white"
            size="lg"
            style={{
              position: "absolute",
              right: 5,
              top: 5,
              padding: "0 5px",
              minWidth: "unset",
            }}
            onClick={onClose}
          >
            <IconX size={32} />
          </ActionIcon>
        )}
        <Portrait profile={trainer} size="xl" who="person" />
        <div>
          <Text size="lg" fw={500}>
            {trainer.firstName} {trainer.lastName}
          </Text>
          <Text size="sm" c="dimmed">
            {trainer.locality}, {trainer.country}
          </Text>
          <Space h={5} />
          <Text fs="italic" lineClamp={3}>
            {trainer.headline}
          </Text>
          <Text td="italic" lineClamp={3}>
            Sessions: {sessionTypeLabel(trainer)}
          </Text>
        </div>
      </Group>
    </Card.Section>

    <Button
      component={Link}
      href={createPath({
        path: UniversalRoutes.TRAINERS.DETAIL,
        params: { trainerId: trainer.id },
      })}
      bg={"blue"}
      fullWidth
    >
      View Profile
    </Button>
  </Card>
);

const SpecializationFilter = ({
  selectedSpecialties,
  setSelectedSpecialties,
  showSpecialtyFilter,
  setShowSpecialtyFilter,
}: {
  selectedSpecialties: string[];
  setSelectedSpecialties: (specialties: string[]) => void;
  showSpecialtyFilter: boolean;
  setShowSpecialtyFilter: (show: boolean) => void;
}) => (
  <Stack>
    <Group>
      <Chip
        checked={selectedSpecialties.length > 0}
        onChange={() => setShowSpecialtyFilter(!showSpecialtyFilter)}
        icon={""}
      >
        <Group gap={4}>
          <Text>
            Specialization
            {selectedSpecialties.length > 0
              ? ` (${selectedSpecialties.length})`
              : ""}
          </Text>
          {showSpecialtyFilter ? (
            <IconChevronUp size={16} />
          ) : (
            <IconChevronDown size={16} />
          )}
        </Group>
      </Chip>
    </Group>

    <Collapse in={showSpecialtyFilter}>
      <Group>
        {specialtyOptions.map((specialty) => (
          <Chip
            key={specialty.value}
            checked={selectedSpecialties.includes(specialty.value)}
            onChange={(checked) => {
              if (checked) {
                setSelectedSpecialties([
                  ...selectedSpecialties,
                  specialty.value,
                ]);
              } else {
                setSelectedSpecialties(
                  selectedSpecialties.filter((s) => s !== specialty.value),
                );
              }
            }}
          >
            {specialty.label}
          </Chip>
        ))}
      </Group>
    </Collapse>
  </Stack>
);

const sessionTypeOptions = [
  { value: "virtual", label: "Virtual" },
  { value: "inPerson", label: "In Person" },
];

const SessionTypeFilter = ({
  selectedSessionTypes,
  setSelectedSessionTypes,
  showSessionTypeFilter,
  setShowSessionTypeFilter,
}: {
  selectedSessionTypes: string[];
  setSelectedSessionTypes: (types: string[]) => void;
  showSessionTypeFilter: boolean;
  setShowSessionTypeFilter: (show: boolean) => void;
}) => {
  const getLabel = () => {
    if (selectedSessionTypes.length === 0) return "In person / Virtual";
    if (selectedSessionTypes.length === 2) return "In Person & Virtual";
    return selectedSessionTypes[0] === "virtual"
      ? "Virtual only"
      : "In person only";
  };

  return (
    <Stack>
      <Group>
        <Chip
          checked={selectedSessionTypes.length > 0}
          onChange={() => setShowSessionTypeFilter(!showSessionTypeFilter)}
          icon={""}
        >
          <Group gap={4}>
            <Text>{getLabel()}</Text>
            {showSessionTypeFilter ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </Group>
        </Chip>
      </Group>

      <Collapse in={showSessionTypeFilter}>
        <Group>
          {sessionTypeOptions.map((type) => (
            <Chip
              key={type.value}
              checked={selectedSessionTypes.includes(type.value)}
              onChange={(checked) => {
                if (checked) {
                  setSelectedSessionTypes([
                    ...selectedSessionTypes,
                    type.value,
                  ]);
                } else {
                  setSelectedSessionTypes(
                    selectedSessionTypes.filter((t) => t !== type.value),
                  );
                }
              }}
            >
              {type.label}
            </Chip>
          ))}
        </Group>
      </Collapse>
    </Stack>
  );
};

const TrainersList = ({
  selectedSpecialties,
  selectedSessionTypes,
}: {
  selectedSpecialties: string[];
  selectedSessionTypes: string[];
}) => {
  const { data } = client.useQuery("get", "/owner/trainers/", {
    params: {
      query: {
        country: getCurrentCountry(),
      },
    },
  });

  if (!data) {
    return <FancyLoader />;
  }

  const filteredTrainers = data.trainers.filter((trainer) => {
    const matchesSpecialties =
      selectedSpecialties.length === 0 ||
      selectedSpecialties.every((specialty) =>
        trainer.specialities.includes(specialty),
      );

    const matchesSessionTypes =
      selectedSessionTypes.length === 0 ||
      selectedSessionTypes.some((type) => {
        if (type === "virtual") return trainer.virtual;
        if (type === "inPerson") return trainer.inPerson;
        return false;
      });

    return matchesSpecialties && matchesSessionTypes;
  });

  return (
    <Stack>
      {filteredTrainers.map((trainer) => (
        <TrainerCard key={trainer.id} trainer={trainer} />
      ))}
    </Stack>
  );
};

const TrainersMap = ({
  selectedSpecialties,
  selectedSessionTypes,
}: {
  selectedSpecialties: string[];
  selectedSessionTypes: string[];
}) => {
  const { data } = client.useQuery("get", "/owner/trainers/", {
    params: {
      query: {
        country: getCurrentCountry(),
      },
    },
  });
  const [selectedTrainer, setSelectedTrainer] =
    useState<TrainerPageOutput | null>(null);

  const apiStatus = useApiLoadingStatus();

  if (!data || apiStatus !== APILoadingStatus.LOADED) {
    return <FancyLoader />;
  }

  const filteredTrainers = data.trainers.filter((trainer) => {
    const matchesSpecialties =
      selectedSpecialties.length === 0 ||
      selectedSpecialties.every((specialty) =>
        trainer.specialities.includes(specialty),
      );

    const matchesSessionTypes =
      selectedSessionTypes.length === 0 ||
      selectedSessionTypes.some((type) => {
        if (type === "virtual") return trainer.virtual;
        if (type === "inPerson") return trainer.inPerson;
        return false;
      });

    return matchesSpecialties && matchesSessionTypes;
  });

  return (
    <Box style={{ height: "75vh", position: "relative" }}>
      <GoogleMap
        style={{ width: "100%", height: "100%" }}
        mapId="2a14027706c15969"
        defaultCenter={{ lat: 52.1462478, lng: 4.7465638 }}
        defaultZoom={10}
        streetViewControl={false}
      >
        {filteredTrainers.map((trainer) => (
          <AdvancedMarker
            key={trainer.id}
            position={trainer.location}
            onClick={() => setSelectedTrainer(trainer)}
          />
        ))}
      </GoogleMap>
      {selectedTrainer && (
        <Box
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            right: 20,
            maxWidth: 400,
            zIndex: 1000,
          }}
        >
          <TrainerCard
            trainer={selectedTrainer}
            withCloseButton={true}
            onClose={() => setSelectedTrainer(null)}
          />
        </Box>
      )}
    </Box>
  );
};

export const TrainersFinder = () => {
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [showSpecialtyFilter, setShowSpecialtyFilter] = useState(false);
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>(
    [],
  );
  const [showSessionTypeFilter, setShowSessionTypeFilter] = useState(false);

  return (
    <Stack gap="md">
      <Title order={1}>Find a trainer</Title>

      <Group>
        <SpecializationFilter
          selectedSpecialties={selectedSpecialties}
          setSelectedSpecialties={setSelectedSpecialties}
          showSpecialtyFilter={showSpecialtyFilter}
          setShowSpecialtyFilter={setShowSpecialtyFilter}
        />
        <SessionTypeFilter
          selectedSessionTypes={selectedSessionTypes}
          setSelectedSessionTypes={setSelectedSessionTypes}
          showSessionTypeFilter={showSessionTypeFilter}
          setShowSessionTypeFilter={setShowSessionTypeFilter}
        />
      </Group>

      <Tabs defaultValue="list">
        <Tabs.List grow justify="center">
          <Tabs.Tab value="list" leftSection={<IconList size={14} />}>
            List View
          </Tabs.Tab>
          <Tabs.Tab value="map" leftSection={<IconMap size={14} />}>
            Map View
          </Tabs.Tab>
        </Tabs.List>

        <Space h="md" />

        <Tabs.Panel value="list">
          <TrainersList
            selectedSpecialties={selectedSpecialties}
            selectedSessionTypes={selectedSessionTypes}
          />
        </Tabs.Panel>

        <Tabs.Panel value="map">
          <TrainersMap
            selectedSpecialties={selectedSpecialties}
            selectedSessionTypes={selectedSessionTypes}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
